import { buildAuthHeaders } from "../../forum/authHeaders";
import { getCookie } from "../../utils/csrf";

export const WELCOME_OFFER_ENDPOINTS = Object.freeze({
  status: "/api/auth/rewards/welcome-offer/status/",
  claim: "/api/auth/rewards/welcome-offer/claim/",
  dismiss: "/api/auth/rewards/welcome-offer/dismiss/",
});

const isLocalFrontend =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

const API_BASE_URL = isLocalFrontend
  ? process.env.REACT_APP_API_BASE_URL_LOCAL || ""
  : process.env.REACT_APP_API_BASE_URL || "";

function apiUrl(path) {
  return API_BASE_URL ? `${API_BASE_URL}${path}` : path;
}

function firstObject(...values) {
  return values.find(
    (value) => value && typeof value === "object" && !Array.isArray(value)
  ) || {};
}

function nullableString(value) {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
}

function nullableInteger(value) {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isSafeInteger(number) ? number : null;
}

export function normalizeTermsUrl(value) {
  const url = nullableString(value);
  if (!url) return null;
  if (url.startsWith("/") && !url.startsWith("//")) return url;
  try {
    const parsed = new URL(url);
    return ["http:", "https:"].includes(parsed.protocol) ? parsed.toString() : null;
  } catch (_error) {
    return null;
  }
}

export function normalizeWelcomeOfferStatus(payload) {
  const source = firstObject(payload?.offer, payload?.data, payload);
  const claimResultSource = firstObject(source.claim_result);
  return {
    campaignActive: source.campaign_active === true,
    eligible: source.eligible === true,
    claimable: source.claimable === true,
    alreadyClaimed: source.already_claimed === true,
    displayAllowed: source.display_allowed === true,
    offerCode: nullableString(source.offer_code),
    amountMinor: nullableInteger(source.amount_minor),
    currency: nullableString(source.currency)?.toUpperCase() || null,
    startsAt: nullableString(source.starts_at),
    expiresAt: nullableString(source.expires_at),
    termsUrl: normalizeTermsUrl(source.terms_url),
    reasonCode: nullableString(source.reason_code),
    offerVersion: nullableString(source.offer_version),
    dismissedUntil: nullableString(source.dismissed_until),
    dismissalScope: nullableString(source.dismissal_scope),
    serverTime: nullableString(source.server_time),
    claimResult:
      Object.keys(claimResultSource).length > 0
        ? normalizeWelcomeOfferClaim(claimResultSource)
        : null,
    raw: source,
  };
}

export function normalizeWelcomeOfferClaim(payload) {
  const source = firstObject(payload?.data, payload);
  const statusSource = firstObject(source?.offer_status, source?.offer, source);
  const legacyWallet = firstObject(source.wallet, source.wallet_summary);
  const legacyWalletTransaction = firstObject(source.wallet_transaction);
  const centralWallet = firstObject(source.central_wallet, legacyWallet);
  const centralWalletTransaction = firstObject(
    source.central_wallet_transaction,
    legacyWalletTransaction
  );
  const canonicalTransactionId = nullableString(
    source.canonical_transaction_id ?? centralWalletTransaction?.id
  );
  const legacyTransactionId = nullableString(
    source.transaction_id ?? legacyWalletTransaction?.id
  );
  return {
    claimId: nullableString(source.claim_id ?? source?.claim?.id),
    transactionId: canonicalTransactionId || legacyTransactionId,
    canonicalTransactionId,
    legacyTransactionId,
    rewardActivityId: nullableString(source.reward_activity_id),
    notificationId: nullableString(
      source.notification_id ?? source?.notification?.id
    ),
    correlationId: nullableString(source.correlation_id),
    status: nullableString(source.status),
    amountMinor: nullableInteger(source.amount_minor),
    currency: nullableString(source.currency)?.toUpperCase() || null,
    // Canonical double-entry records are primary. The legacy fields remain
    // available only while older wallet consumers complete their server sync.
    wallet: centralWallet,
    walletTransaction: centralWalletTransaction,
    legacyWallet,
    legacyWalletTransaction,
    reward: firstObject(source.reward, source.reward_summary),
    notification: firstObject(source.notification),
    offerStatus: normalizeWelcomeOfferStatus(statusSource),
    raw: source,
  };
}

export class WelcomeOfferRequestError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = "WelcomeOfferRequestError";
    this.code = options.code || "RECOVERABLE";
    this.status = options.status || 0;
    this.reasonCode = options.reasonCode || null;
    this.payload = options.payload;
    this.cause = options.cause;
  }
}

async function readPayload(response) {
  const contentType = response.headers?.get?.("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json().catch(() => ({}));
  }
  return response.text().catch(() => "");
}

function requestMessage(payload, fallback) {
  if (!payload || typeof payload !== "object") return fallback;
  // Only stable, user-facing fields are considered. Internal antifraud details
  // remain deliberately ignored by the client.
  return String(payload.user_message || payload.message || payload.detail || fallback);
}

function responseError(response, payload, intent) {
  const status = Number(response.status) || 0;
  const reasonCode = nullableString(payload?.reason_code || payload?.code);
  let code = "RECOVERABLE";
  if (status === 401) code = "SESSION_EXPIRED";
  if (status === 409 || status === 410) code = "RECONCILE_REQUIRED";
  if (intent === "claim" && [500, 502, 503, 504].includes(status)) {
    code = "UNCERTAIN";
  }
  return new WelcomeOfferRequestError(
    requestMessage(payload, "Le service est momentanément indisponible."),
    { code, status, reasonCode, payload }
  );
}

async function requestWelcomeOffer(path, { method = "GET", idempotencyKey, signal, intent } = {}) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    throw new WelcomeOfferRequestError(
      "Une connexion internet est nécessaire pour continuer.",
      { code: "OFFLINE" }
    );
  }

  const csrfToken = typeof document === "undefined" ? null : getCookie("csrftoken");
  const headers = buildAuthHeaders({
    Accept: "application/json",
    ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
    ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
  });

  let response;
  try {
    response = await fetch(apiUrl(path), {
      method,
      headers,
      signal,
      credentials: "same-origin",
    });
  } catch (cause) {
    const wentOffline =
      typeof navigator !== "undefined" && navigator.onLine === false;
    const code = intent === "claim" ? "UNCERTAIN" : wentOffline ? "OFFLINE" : "RECOVERABLE";
    throw new WelcomeOfferRequestError(
      code === "UNCERTAIN"
        ? "La réponse de l’activation n’a pas pu être confirmée."
        : "Le service est momentanément inaccessible.",
      { code, cause }
    );
  }

  const payload = await readPayload(response);
  if (!response.ok) throw responseError(response, payload, intent);
  return payload;
}

export async function getWelcomeOfferStatus(options = {}) {
  const payload = await requestWelcomeOffer(WELCOME_OFFER_ENDPOINTS.status, {
    method: "GET",
    signal: options.signal,
    intent: "status",
  });
  return normalizeWelcomeOfferStatus(payload);
}

export async function claimWelcomeOffer({ idempotencyKey, signal } = {}) {
  if (!idempotencyKey) {
    throw new WelcomeOfferRequestError(
      "La demande d’activation n’a pas pu être sécurisée.",
      { code: "RECOVERABLE" }
    );
  }
  const payload = await requestWelcomeOffer(WELCOME_OFFER_ENDPOINTS.claim, {
    method: "POST",
    idempotencyKey,
    signal,
    intent: "claim",
  });
  return normalizeWelcomeOfferClaim(payload);
}

export async function dismissWelcomeOffer(options = {}) {
  const payload = await requestWelcomeOffer(WELCOME_OFFER_ENDPOINTS.dismiss, {
    method: "POST",
    signal: options.signal,
    intent: "dismiss",
  });
  return {
    persisted: payload?.persisted === true,
    dismissalScope: nullableString(payload?.dismissal_scope),
    dismissedUntil: nullableString(payload?.dismissed_until),
    raw: payload,
  };
}

const welcomeOfferService = {
  getStatus: getWelcomeOfferStatus,
  claim: claimWelcomeOffer,
  dismiss: dismissWelcomeOffer,
};

export default welcomeOfferService;
